// apps/admin/src/lib/errorHandler.ts
//
// Phase 8.1 Wave 2 Task 08 — canonical Frontend error helpers for the
// admin portal. Implements the INTERFACE-STANDARDS.md message-priority
// chain: `error.user_message > error.message > message > network_fallback`.
//
// Why a dedicated module:
//   - Toasts, banners, and form-error glue all need the SAME priority
//     resolver. Inlining the chain in every consumer drifts; one helper
//     keeps the rule machine-checkable and lets tests pin the priority
//     order without re-reading the standard each time.
//   - `apiClient.ts` throws `ApiError` carrying the canonical envelope.
//     Consumers either catch `ApiError` directly OR receive an unknown
//     `err` from a third-party (axios native, fetch, custom thrown
//     strings). `getUserMessage` accepts ANY input and resolves the best
//     user-facing copy without leaking AxiosError.message / statusText
//     (banned by INTERFACE-STANDARDS frontend.toast_rule).
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-160
// vg-binding: P8.D-162
// vg-binding: P8.D-174
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Canonical error envelope per INTERFACE-STANDARDS.md §error-shape.
 * Re-exported by `apiClient.ts` for downstream callers.
 */
export interface ApiErrorEnvelope {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    details?: Record<string, unknown>;
    field_errors?: Record<string, string[]>;
    request_id?: string;
  };
}

/**
 * Network-fallback copy when the API gave us no body / no message at
 * all. Neutral English default — this defensive const is only hit when
 * no API-side message survived the priority chain, and locale-aware
 * callers override it with their own translated copy.
 */
export const NETWORK_FALLBACK_MESSAGE =
  'Unable to connect to the server. Please try again.';

/**
 * Resolve the best user-facing message for any thrown / returned
 * error-like object following the INTERFACE-STANDARDS priority chain.
 *
 * Resolution order:
 *   1. `err.userMessage` (already-unpacked ApiError field)
 *   2. `err.error.user_message` (raw envelope)
 *   3. `err.error.message` (raw envelope safe default)
 *   4. `err.message` (top-level non-Axios string)
 *   5. `NETWORK_FALLBACK_MESSAGE`
 *
 * NEVER returns `Request failed with status …` / AxiosError.message /
 * Response.statusText — those are banned by the frontend standard.
 */
export function getUserMessage(err: unknown): string {
  if (err == null) return NETWORK_FALLBACK_MESSAGE;

  // String thrown directly — accept as-is unless it smells like a
  // banned HTTP framing message.
  if (typeof err === 'string') {
    return looksLikeBannedHttpMessage(err) ? NETWORK_FALLBACK_MESSAGE : err;
  }

  if (typeof err === 'object') {
    const e = err as any;

    // 1. ApiError instance (already unpacked).
    if (typeof e.userMessage === 'string' && e.userMessage.length > 0) {
      return e.userMessage;
    }

    // 2. Raw envelope `.error.user_message`.
    const envUser = e?.error?.user_message;
    if (typeof envUser === 'string' && envUser.length > 0) {
      return envUser;
    }

    // 3. Raw envelope `.error.message`.
    const envMsg = e?.error?.message;
    if (typeof envMsg === 'string' && envMsg.length > 0) {
      return envMsg;
    }

    // 4. ApiError.message (constructor already picked user_message > message).
    if (typeof e.message === 'string' && e.message.length > 0) {
      return looksLikeBannedHttpMessage(e.message)
        ? NETWORK_FALLBACK_MESSAGE
        : e.message;
    }
  }

  return NETWORK_FALLBACK_MESSAGE;
}

/**
 * Extract `field_errors` map (field → first-message) from any error
 * shape. Returns null when no field errors are present — consumers use
 * this for inline form validation (`<Field error={fieldErrors.email}/>`).
 */
export function getFieldErrors(
  err: unknown,
): Record<string, string> | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as any;

  // Prefer already-unpacked ApiError.fieldErrors.
  const fe = e.fieldErrors ?? e.error?.field_errors;
  if (!fe || typeof fe !== 'object') return null;

  const out: Record<string, string> = {};
  for (const [field, msgs] of Object.entries(fe)) {
    if (Array.isArray(msgs) && msgs.length > 0 && typeof msgs[0] === 'string') {
      out[field] = msgs[0];
    } else if (typeof msgs === 'string') {
      out[field] = msgs;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Extract `error.code` (machine-readable) from any error shape. Used
 * by branching consumers (e.g. `if (code === 'SESSION_EXPIRED')`).
 */
export function getErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const e = err as any;
  if (typeof e.code === 'string' && e.code.length > 0) return e.code;
  const envCode = e?.error?.code;
  if (typeof envCode === 'string' && envCode.length > 0) return envCode;
  return null;
}

/**
 * Detect the framing-only strings that INTERFACE-STANDARDS bans from
 * the toast/banner surfaces (`Request failed with status ...`,
 * raw Axios network framing, Response.statusText defaults).
 *
 * Treat anything matching one of these patterns as if the API gave us
 * NO message — fall through to `NETWORK_FALLBACK_MESSAGE` instead.
 */
function looksLikeBannedHttpMessage(s: string): boolean {
  if (s.startsWith('Request failed with status')) return true;
  if (s === 'Network Error') return true;
  if (/^HTTP_\d{3}$/.test(s)) return true;
  if (/^Status:\s*\d{3}/i.test(s)) return true;
  return false;
}
